import BackendService from 'BackendService';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useRecentlyViewed from '../../hooks/admin/useRecentlyViewed';
import { EntityManagementState, initialEntityManagementState } from './entityManagementTypes';

export function useEntityManagement() {
  const { entityId } = useParams<{ entityId: string }>();
  const { trackView } = useRecentlyViewed();
  const [state, setState] = useState<EntityManagementState>(initialEntityManagementState);

  const loadEntity = useCallback(async () => {
    if (!entityId) return;

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const backendService = new BackendService();

      const [entity, contacts, allocations] = await Promise.all([
        backendService.getInvestmentEntity(entityId),
        backendService.getEntityContacts(entityId),
        backendService.getEntityAllocations(entityId),
      ]);

      setState((prev) => ({
        ...prev,
        entity,
        contacts,
        allocations,
        loading: false,
        editedEntity: {
          name: entity.name,
          description: entity.description || '',
          isForeignInvestor: entity.isForeignInvestor || false,
        },
      }));

      if (entity) {
        trackView('INVESTMENT_ENTITY', entity.id);
      }
    } catch (error) {
      console.error('Error loading entity:', error);
      setState((prev) => ({
        ...prev,
        error: 'Failed to load entity details',
        loading: false,
      }));
    }
  }, [entityId, trackView]);

  useEffect(() => {
    if (entityId) {
      loadEntity();
    }
  }, [entityId, loadEntity]);

  const handleEditToggle = () => {
    setState((prev) => ({
      ...prev,
      isEditing: !prev.isEditing,
      editedEntity: prev.entity
        ? {
            name: prev.entity.name,
            description: prev.entity.description || '',
            isForeignInvestor: prev.entity.isForeignInvestor || false,
          }
        : prev.editedEntity,
    }));
  };

  const handleSaveChanges = async () => {
    if (!state.entity) return;

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const backendService = new BackendService();
      await backendService.updateInvestmentEntity(state.entity.id, {
        name: state.editedEntity.name,
        description: state.editedEntity.description,
        isForeignInvestor: state.editedEntity.isForeignInvestor,
      });

      // Reload from the backend rather than trusting the PATCH response's
      // shape directly — keeps this resilient to whatever that endpoint
      // returns, and picks up any other server-side changes at the same time.
      setState((prev) => ({ ...prev, isEditing: false }));
      await loadEntity();
    } catch (error) {
      console.error('Error updating entity:', error);
      setState((prev) => ({
        ...prev,
        error: 'Failed to update entity',
        loading: false,
      }));
    }
  };

  const handleInputChange = (field: 'name' | 'description', value: string) => {
    setState((prev) => ({
      ...prev,
      editedEntity: { ...prev.editedEntity, [field]: value },
    }));
  };

  const handleForeignInvestorToggle = (value: boolean) => {
    setState((prev) => ({
      ...prev,
      editedEntity: { ...prev.editedEntity, isForeignInvestor: value },
    }));
  };

  const handleManageContacts = () => {
    setState((prev) => ({
      ...prev,
      showContactAssociation: !prev.showContactAssociation,
    }));
  };

  const handleContactAssociationComplete = () => {
    setState((prev) => ({ ...prev, showContactAssociation: false }));
    loadEntity();
  };

  const handlePromoteToOwner = async (contactId: string) => {
    if (!state.entity) return;

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const backendService = new BackendService();
      await backendService.promoteContactToOwner(state.entity.id, contactId);
      await loadEntity();
    } catch (error) {
      console.error('Error promoting contact to owner:', error);
      setState((prev) => ({
        ...prev,
        error: 'Failed to promote contact to owner',
        loading: false,
      }));
    }
  };

  const toggleSection = (section: keyof typeof state.expandedSections) => {
    setState((prev) => ({
      ...prev,
      expandedSections: {
        ...prev.expandedSections,
        [section]: !prev.expandedSections[section],
      },
    }));
  };

  return {
    state,
    handleEditToggle,
    handleSaveChanges,
    handleInputChange,
    handleForeignInvestorToggle,
    handleManageContacts,
    handleContactAssociationComplete,
    handlePromoteToOwner,
    toggleSection,
  };
}
